import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, resolveAssetUrl } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import Reviews from "@/components/Reviews";
import SizeGuideModal from "@/components/SizeGuideModal";
import Stars from "@/components/Stars";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { Loader2, ChevronLeft } from "lucide-react";

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const { add } = useCart();
  const [ratingSummary, setRatingSummary] = useState({ average: 0, count: 0 });

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        setActiveImg(0);
        const variants = r.data?.variants || [];
        if (variants.length > 0) {
          setSize(variants[0].size);
          setColor(variants[0].color);
        }
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
    api
      .get(`/products/${id}/reviews`)
      .then((r) => setRatingSummary({ average: r.data.average, count: r.data.count }))
      .catch(() => {});
  }, [id]);

  const sizes = useMemo(
    () =>
      product?.variants
        ? [...new Set(product.variants.map((v) => v.size))]
        : [],
    [product]
  );
  const colors = useMemo(
    () =>
      product?.variants
        ? [...new Set(product.variants.map((v) => v.color))]
        : [],
    [product]
  );
  const selectedVariant = useMemo(
    () =>
      product?.variants?.find((v) => v.size === size && v.color === color),
    [product, size, color]
  );
  const stock = selectedVariant?.stock ?? 0;

  const handleAdd = () => {
    if (product.variants?.length && (!size || !color)) {
      toast.error("Please select size and color");
      return;
    }
    if (selectedVariant && stock < qty) {
      toast.error(`Only ${stock} left in ${size}/${color}`);
      return;
    }
    add(product, { quantity: qty, size, color });
    toast.success("Added to your gear bag");
  };

  const gallery = useMemo(() => {
    if (!product) return [];
    const arr = [product.image_url, ...(product.gallery || [])];
    return [...new Set(arr)].filter(Boolean).map(resolveAssetUrl);
  }, [product]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b0b0b] flex items-center justify-center">
        <Loader2 className="animate-spin text-white" size={32} />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#0b0b0b] text-white">
        <Header />
        <CartDrawer />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <h1 className="font-heading text-4xl uppercase">Not Found</h1>
          <Link to="/" className="text-zinc-400 underline">
            Back to shop
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="overflow-x-hidden" data-testid="product-detail-page">
      <Header />
      <CartDrawer />

      <div className="max-w-7xl mx-auto px-6 py-10">
        <Link
          to="/#shop"
          className="text-xs tracking-widest uppercase text-zinc-500 hover:text-white inline-flex items-center gap-2 mb-8"
          data-testid="product-back-link"
        >
          <ChevronLeft size={14} /> Back to Shop
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Gallery */}
          <div className="space-y-3">
            <div
              className="bg-gradient-to-br from-zinc-900 to-zinc-950 aspect-[3/4] overflow-hidden flex items-center justify-center"
              data-testid="product-main-image"
            >
              <img
                src={gallery[activeImg]}
                alt={product.name}
                className="w-full h-full object-contain p-4"
              />
            </div>
            {gallery.length > 1 && (
              <div className="grid grid-cols-4 gap-3">
                {gallery.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`aspect-square overflow-hidden border-2 bg-gradient-to-br from-zinc-900 to-zinc-950 flex items-center justify-center ${
                      i === activeImg
                        ? "border-white"
                        : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                    data-testid={`product-thumb-${i}`}
                  >
                    <img
                      src={src}
                      alt=""
                      className="w-full h-full object-contain p-1.5"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="space-y-6">
            <div>
              {product.badge && (
                <span
                  className="inline-block bg-white text-black text-[10px] tracking-widest uppercase px-2 py-1 mb-3 font-bold"
                  data-testid="product-detail-badge"
                >
                  {product.badge}
                </span>
              )}
              <h1
                className="font-heading text-5xl font-black uppercase tracking-tight leading-none"
                data-testid="product-detail-name"
              >
                {product.name}
              </h1>
              <p
                className="text-3xl font-heading font-bold mt-4 metallic-text"
                data-testid="product-detail-price"
              >
                ${Number(product.price).toFixed(2)}
              </p>
              {ratingSummary.count > 0 && (
                <div
                  className="flex items-center gap-2 mt-3"
                  data-testid="pdp-rating-summary"
                >
                  <Stars rating={ratingSummary.average} size={14} />
                  <span className="text-xs text-zinc-400 tracking-wider">
                    {ratingSummary.average.toFixed(1)} ·{" "}
                    <a href="#reviews" className="underline">
                      {ratingSummary.count} review
                      {ratingSummary.count !== 1 ? "s" : ""}
                    </a>
                  </span>
                </div>
              )}
            </div>

            <p
              className="text-zinc-400 text-sm leading-relaxed"
              data-testid="product-detail-description"
            >
              {product.description}
            </p>

            {colors.length > 0 && (
              <div className="space-y-2">
                <div className="text-[10px] tracking-widest uppercase text-zinc-500">
                  Color · <span className="text-white">{color}</span>
                </div>
                <div className="flex gap-3">
                  {colors.map((c) => (
                    <button
                      key={c}
                      onClick={() => setColor(c)}
                      className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
                        c === color
                          ? "border-white bg-white text-black"
                          : "border-zinc-700 text-zinc-300 hover:border-zinc-500"
                      }`}
                      data-testid={`product-color-${c}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {sizes.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] tracking-widest uppercase text-zinc-500">
                    Size · <span className="text-white">{size}</span>
                  </div>
                  <SizeGuideModal category={product.category} />
                </div>
                <div className="flex gap-2 flex-wrap">
                  {sizes.map((s) => {
                    const variant = product.variants.find(
                      (v) => v.size === s && v.color === color
                    );
                    const outOfStock = !variant || variant.stock <= 0;
                    return (
                      <button
                        key={s}
                        onClick={() => setSize(s)}
                        disabled={outOfStock}
                        className={`w-12 h-12 text-xs font-bold uppercase tracking-wider border transition-colors ${
                          s === size
                            ? "border-white bg-white text-black"
                            : outOfStock
                            ? "border-zinc-900 text-zinc-700 cursor-not-allowed line-through"
                            : "border-zinc-700 text-zinc-300 hover:border-zinc-500"
                        }`}
                        data-testid={`product-size-${s}`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div className="text-[10px] tracking-widest uppercase text-zinc-500">
                Quantity
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="w-10 h-10 border border-zinc-700 hover:bg-zinc-900"
                  data-testid="product-qty-decrease"
                >
                  −
                </button>
                <span
                  className="w-10 text-center font-bold"
                  data-testid="product-qty-value"
                >
                  {qty}
                </span>
                <button
                  onClick={() => setQty(qty + 1)}
                  className="w-10 h-10 border border-zinc-700 hover:bg-zinc-900"
                  data-testid="product-qty-increase"
                >
                  +
                </button>
                <span className="text-[10px] tracking-widest uppercase text-zinc-500 ml-3">
                  {selectedVariant
                    ? stock > 0
                      ? `${stock} in stock`
                      : "Out of stock"
                    : ""}
                </span>
              </div>
            </div>

            <button
              onClick={handleAdd}
              disabled={selectedVariant && stock <= 0}
              className="w-full bg-white text-black font-heading font-bold text-base tracking-widest uppercase py-4 hover:bg-zinc-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="product-add-to-cart-btn"
            >
              {selectedVariant && stock <= 0
                ? "Sold Out"
                : "Add to Gear Bag"}
            </button>

            <div className="border-t border-zinc-900 pt-6 space-y-3 text-xs text-zinc-500 tracking-wide">
              <div className="flex justify-between">
                <span className="uppercase tracking-widest text-zinc-600">Free Shipping</span>
                <span>Orders over $100</span>
              </div>
              <div className="flex justify-between">
                <span className="uppercase tracking-widest text-zinc-600">Returns</span>
                <span>30-day no-questions</span>
              </div>
              <div className="flex justify-between">
                <span className="uppercase tracking-widest text-zinc-600">Built On</span>
                <span>Faith. Driven by Purpose.</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-12" id="reviews">
        <Reviews productId={product.id} />
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetail;
